/**
 * TestCaseDetailHeader — top bar of the detail pane.
 * Contains: tcId badge, inline-editable title, severity select,
 * type badge, expand button, clone/delete/close actions.
 *
 * The X (close) button is anchored top-right for both inline
 * and fullscreen modes.
 */

import { useState, useEffect } from 'react';
import { Maximize2, Minimize2, Copy, Trash2, Loader2, X } from 'lucide-react';
import { SeverityBadge } from '../shared/SeverityBadge';
import type { TestSeverity, TestType } from '../types';
import { ALL_SEVERITIES, SEVERITY_LABELS } from '../types';

interface TestCaseDetailHeaderProps {
    tcId?:         string;
    title:         string;
    setTitle:      (v: string) => void;
    severity:      TestSeverity;
    setSeverity:   (v: TestSeverity) => void;
    type:          TestType;
    setType:       (v: TestType) => void;
    isNew:         boolean;
    isFullscreen?: boolean;
    onExpand?:     () => void;
    onClose?:      () => void;
    onDelete?:     () => void;
    onClone?:      () => void;
    isDeleting?:   boolean;
    isCloning?:    boolean;
}

const TYPE_STYLES: Record<TestType, string> = {
    MANUAL:    'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-100',
    AUTOMATED: 'bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-100',
};

export function TestCaseDetailHeader({
    tcId,
    title,
    setTitle,
    severity,
    setSeverity,
    type,
    setType,
    isNew,
    isFullscreen = false,
    onExpand,
    onClose,
    onDelete,
    onClone,
    isDeleting,
    isCloning,
}: TestCaseDetailHeaderProps) {
    const [editingTitle, setEditingTitle] = useState(false);
    const [localTitle,   setLocalTitle]   = useState(title);

    useEffect(() => { setLocalTitle(title); }, [title]);

    function commitTitle() {
        setEditingTitle(false);
        if (localTitle.trim()) setTitle(localTitle.trim());
        else setLocalTitle(title); // revert if empty
    }

    return (
        <div data-testid="test-case-detail-header" className="flex flex-col gap-2 px-4 py-3 border-b border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark flex-shrink-0">
            {/* Row 1: badges + action buttons */}
            <div className="flex items-center gap-1.5 min-w-0">
                {/* tcId / New badge */}
                {tcId && !isNew && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 flex-shrink-0">
                        {tcId}
                    </span>
                )}
                {isNew && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex-shrink-0">
                        New
                    </span>
                )}

                <div className="flex-1" />

                {/* Clone */}
                {onClone && !isNew && (
                    <button
                        data-testid="header-clone-button"
                        aria-label="Clone test case"
                        onClick={onClone}
                        disabled={isCloning}
                        title="Clone test case"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                    >
                        {isCloning ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
                    </button>
                )}

                {/* Delete */}
                {onDelete && !isNew && (
                    <button
                        data-testid="header-delete-button"
                        aria-label="Delete test case"
                        onClick={onDelete}
                        disabled={isDeleting}
                        title="Delete test case"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors flex-shrink-0"
                    >
                        {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                )}

                {/* Expand / Collapse — hidden on mobile (detail is already fullscreen there) */}
                {onExpand && (
                    <button
                        data-testid="header-expand-button"
                        aria-label={isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'}
                        onClick={onExpand}
                        title={isFullscreen ? 'Exit fullscreen (F)' : 'Expand to fullscreen (F)'}
                        className="hidden md:flex items-center justify-center p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                    >
                        {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                    </button>
                )}

                {/* Close — always top-right, prominent on mobile */}
                {onClose && (
                    <button
                        data-testid="header-close-button"
                        aria-label="Close"
                        onClick={onClose}
                        title="Close"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                    >
                        <X size={15} />
                    </button>
                )}
            </div>

            {/* Row 2: Title */}
            {editingTitle ? (
                <input
                    autoFocus
                    value={localTitle}
                    onChange={(e) => setLocalTitle(e.target.value)}
                    onBlur={commitTitle}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter')  commitTitle();
                        if (e.key === 'Escape') { setLocalTitle(title); setEditingTitle(false); }
                    }}
                    className="w-full text-sm font-semibold text-slate-800 dark:text-slate-100 bg-transparent border-b-2 border-blue-500 focus:outline-none py-0.5"
                />
            ) : (
                <h2
                    className="text-sm font-semibold text-slate-800 dark:text-slate-100 cursor-text hover:text-blue-600 dark:hover:text-blue-400 transition-colors line-clamp-2 min-w-0"
                    title="Click to edit title"
                    onClick={() => setEditingTitle(true)}
                >
                    {title || <span className="text-slate-400 font-normal">Untitled — click to set title</span>}
                </h2>
            )}

            {/* Row 3: Severity + Type */}
            <div className="flex items-center gap-2 flex-wrap">
                {/* Severity selector */}
                <select
                    aria-label="Severity"
                    data-testid="header-severity-select"
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value as TestSeverity)}
                    className="text-xs border border-slate-200 dark:border-gh-border-dark rounded-lg px-2 py-1 bg-white dark:bg-gh-bg-dark text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                >
                    {ALL_SEVERITIES.map((s) => (
                        <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>
                    ))}
                </select>

                {/* Type selector */}
                <select
                    aria-label="Type"
                    data-testid="header-type-select"
                    value={type}
                    onChange={(e) => setType(e.target.value as TestType)}
                    className={`text-xs border border-transparent rounded-lg px-2 py-1 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${TYPE_STYLES[type]}`}
                >
                    <option value="MANUAL">Manual</option>
                    <option value="AUTOMATED">Automated</option>
                </select>
            </div>
        </div>
    );
}
